// Landings SEO (bodas, comunión por intención de búsqueda). El contenido vive
// en landings.json y lo usan tanto la página React como scripts/build-seo-pages.mjs,
// que genera dist/<slug>/index.html con el HTML ya renderizado para buscadores.
// Imports relativos (sin '@/'): este módulo también se empaqueta con esbuild.
import data from './landings.json';
import dataEn from './landings.en.json';

export interface LandingImage { src: string; alt: string; caption?: string; w?: number; h?: number }
export interface LandingLink { label: string; href: string }
export interface LandingSection { h2: string; p?: string[]; list?: string[] }
export interface Landing {
  slug: string;
  group: 'boda' | 'comunion' | 'navidad';
  nav: string;
  title: string;
  description: string;
  kicker: string;
  h1: string;
  intro: string[];
  images: LandingImage[];
  // Ejemplos de diseño que se muestran en rejilla al final de la página.
  galeria?: LandingImage[];
  // Texto bajo el título de la galería, si el genérico (diseños de IA) no encaja.
  galeriaNota?: string;
  // Idioma de la página y slug de su equivalente en el otro idioma (hreflang).
  lang?: 'es' | 'en';
  alt?: string;
  price: PrecioKey;
  // Precio fijo que sustituye a precios[price], para productos que no son una
  // figura del configurador (p. ej. los packs de escenario, src/lib/escenarios.ts).
  precioDesde?: number;
  priceLabel: string;
  cta: LandingLink;
  cta2?: LandingLink;
  sections: LandingSection[];
  faq: { q: string; a: string }[];
  related: string[];
}

export type PrecioKey = 'mini' | 'classic' | 'premium' | 'pareja' | 'llavero';
export type Precios = Record<PrecioKey, number>;

export const LANDINGS = (data as Landing[]).map(l => ({ ...l, lang: 'es' as const }));
// Páginas de boda en inglés (/en/<slug>): el taller acepta pedidos
// internacionales, y las bodas no son un mercado estacional español.
export const LANDINGS_EN = (dataEn as Landing[]).map(l => ({ ...l, lang: 'en' as const }));
export const TODAS_LAS_LANDINGS: Landing[] = [...LANDINGS, ...LANDINGS_EN];

// Slug equivalente en el otro idioma, para los hreflang.
export function equivalente(l: Landing): Landing | undefined {
  if (l.lang === 'en') return LANDINGS.find(x => x.slug === l.alt);
  return LANDINGS_EN.find(x => x.alt === l.slug);
}

// Solo si /api/site/prices no responde (build sin red o API caída).
export const PRECIOS_FALLBACK: Precios = { mini: 24.9, classic: 39.4, premium: 54.9, pareja: 74.9, llavero: 5.95 };

export function normalizarPrecios(raw: unknown): Precios {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const out = { ...PRECIOS_FALLBACK };
  for (const k of Object.keys(out) as PrecioKey[]) {
    const v = Number(r[k]);
    if (Number.isFinite(v) && v > 0) out[k] = v;
  }
  return out;
}

export const formatPrecio = (n: number) => `${n.toFixed(2).replace('.', ',')} €`;

// Sustituye los marcadores {precio:pareja} del texto por el precio vigente.
export const conPrecios = (texto: string, precios: Precios) =>
  texto.replace(/\{precio:(\w+)\}/g, (m, k: string) => (k in precios ? formatPrecio(precios[k as PrecioKey]) : m));

export function findLanding(pathname: string): Landing | undefined {
  const ruta = pathname.replace(/^\/+|\/+$/g, '');
  if (ruta.startsWith('en/')) return LANDINGS_EN.find(l => l.slug === ruta.slice(3));
  return LANDINGS.find(l => l.slug === ruta);
}
